import { Filter, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

interface FilterDropdownProps {
  label: string
  value: string | null
  options: readonly string[] | string[]
  onChange: (value: string | null) => void
}

export function FilterDropdown({
  label,
  value,
  options,
  onChange,
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={dropdownRef} className="relative">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsOpen(!isOpen)
          }
        }}
        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
          value
            ? 'border-primary/60 bg-primary/10 text-primary dark:border-primary/40 dark:bg-primary/20 dark:text-primary/90'
            : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
      >
        <Filter size={14} />
        {value || label}
        {value && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onChange(null)
            }}
            className="hover:bg-primary/20 -mr-1 ml-1 rounded p-0.5"
            aria-label={`Clear ${label} filter`}
          >
            <X size={12} />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="bg-popover border-border absolute top-full left-0 z-30 mt-1 max-h-[300px] min-w-[160px] overflow-auto rounded-lg border py-1 shadow-xl">
          <button
            type="button"
            onClick={() => {
              onChange(null)
              setIsOpen(false)
            }}
            className={`flex w-full items-center px-3 py-1.5 text-left text-sm transition-colors ${
              !value
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            All
          </button>
          {options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                onChange(option)
                setIsOpen(false)
              }}
              className={`flex w-full items-center px-3 py-1.5 text-left text-sm transition-colors ${
                value === option
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <span className="truncate">{option}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
